/* ============================================================================
   Printing

   The PDF is made by the browser, not by us. `window.print()` on a page whose
   print stylesheet hides everything except the document produces a real PDF
   through "Save as PDF" (or "Open in Preview" on a Mac), with selectable text
   and correct page breaks.

   The one piece of chrome we control is the suggested filename, which browsers
   take from `document.title`. So the title is swapped for the duration of the
   print and put back afterwards.
   ============================================================================ */

/**
 * Prints whatever is currently inside `.doc-print-root`.
 *
 * @param filename Suggested name, without extension. Browsers use the document
 *   title for the default "Save as PDF" name; there is no API for it.
 */
export function printDocument(filename: string) {
  const original = document.title
  const safe = filename.trim() || 'Encore'

  // The title has to be in place before the print dialog opens, and must not
  // be restored until it closes. `afterprint` fires on cancel as well as on
  // save, and the timeout is the fallback for browsers that don't fire it.
  document.title = safe

  let restored = false
  const restore = () => {
    if (restored) return
    restored = true
    document.title = original
    window.removeEventListener('afterprint', restore)
  }

  window.addEventListener('afterprint', restore)
  window.setTimeout(restore, 60_000)

  // A frame's grace so the swapped title lands before the dialog snapshots it.
  window.requestAnimationFrame(() => {
    window.print()
  })
}

/**
 * Shares a link, preferring the platform sheet and falling back to the
 * clipboard.
 *
 * `navigator.share` rejects when the user dismisses the sheet, which is not an
 * error worth surfacing — the caller gets `'dismissed'` and should say nothing.
 */
export async function shareLink(input: {
  title: string
  text?: string
  url: string
}): Promise<'shared' | 'copied' | 'dismissed' | 'failed'> {
  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ title: input.title, text: input.text, url: input.url })
      return 'shared'
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return 'dismissed'
      // Anything else falls through to the clipboard rather than dead-ending.
    }
  }

  try {
    await navigator.clipboard.writeText(input.url)
    return 'copied'
  } catch {
    return 'failed'
  }
}
